import React from 'react'

import HeaderMeta from 'components/HeaderMeta'

import Content, { Theme as ContentTheme } from 'components/base/Content'
import Img from 'components/base/Img'
import Paragraph, { Theme as ParagraphTheme } from 'components/base/Paragraph'
import Title from 'components/base/Title'
import Link from 'components/base/Link'
import Faq from 'components/blocks/Faq'
import List, { Theme as ListTheme } from 'components/base/List'
import SectionRule from 'components/base/SectionRule/styles'
import InsuranceTypeIcon, { Theme as InsuranceTypeIconTheme } from 'components/base/Icon/InsuranceTypeIcon'

import PageHero, { Theme as PageHeroTheme } from 'components/blocks/PageHero'
import AskQuestion from 'components/blocks/AskQuestion'
import IconGrid from 'components/base/Icon/Grid'
import { Theme as IconTheme } from 'components/base/Icon/Grid/Icon'

import MoneyPig from 'assets/images/products/MoneyPig.svg'
import BusinessType from 'assets/images/products/BusinessType.svg'

const ProductsBusinessInsurance: React.FC = (): React.ReactElement => (
  <div>
    <HeaderMeta
      title='Business Insurance | Insurance Lounge'
      description='Need business insurance? Speak with one of our helpful Insurance Concierges to discuss protection options for your business today.'
    />
    <PageHero
      cta={true}
      ctaTitle='Get a business insurance quote now.'
      theme={PageHeroTheme.business}
      trackingInfo={{ page_category: 'product_page_business' }}
    >
      Business Insurance: Plans that Can Protect Your Company
    </PageHero>
    <Content short={true} margined={true} center={true}>
      <InsuranceTypeIcon theme={InsuranceTypeIconTheme.business} colored={true} hasMargin={true} />
      <Paragraph theme={ParagraphTheme.introLarge}>
        We understand that no two businesses are alike. Each business has its own unique needs and challenges based on its industry, location, growth plan, and more.
        Business owners can speak with one of our Insurance Concierges to discuss options when it comes to commercial insurance and the future protection of their businesses.
        Here are a few factors to discuss that can impact your business insurance rate.
      </Paragraph>
      <Title level={2}>What Does Business Insurance Protect?</Title>
      <Paragraph>
        Business insurance policies are designed to help recover damages when unexpected events strike that can affect your ability to resume normal operations,
        which can range from severe flooding to simple property damage. Events that hinder the ability to get work done can impose costly damage
        that might not be feasible to cover out-of-pocket, such as:
      </Paragraph>
      <IconGrid
        icons={[
          {
            theme: IconTheme.wind,
            title: '',
          },
          {
            theme: IconTheme.burstPipe,
            title: '',
          },
          {
            theme: IconTheme.burglary,
            title: '',
          },
          {
            theme: IconTheme.computerDamage,
            title: '',
          },
        ]}
      />
    </Content>
    <Content theme={ContentTheme.yellow}>
      <Content theme={ContentTheme.paddedBelow} center={true} short={true}>
        <Img
          src={MoneyPig}
          src2x={undefined}
          style={{
            height: 240,
            width: 'auto',
          }}
          alt='Money Pig'
        />
      </Content>
      <Content center={true} short={true}>
        <Title level={2}>How Much Does Business Insurance Cost?</Title>
        <Paragraph>
          A business insurance premium varies by coverage needed and the policyholder’s geographic region.
          The cost depends on the type of policy required, which may differ depending on:
        </Paragraph>
        <List theme={ListTheme.oneColumn}>
          <li>The number of employees working at your business</li>
          <li>Your business’s location</li>
          <li>Your coverage needs</li>
        </List>
        <Paragraph>
          Additional factors that contribute to your business’s costs: equipment, property, location,
          and how long the business has been operating
        </Paragraph>
      </Content>
    </Content>
    <Content theme={ContentTheme.gray}>
      <Content theme={ContentTheme.paddedBelow} center={true} short={true}>
        <Img
          src={BusinessType}
          src2x={undefined}
          style={{
            height: 200,
            width: 'auto',
          }}
          alt='Business Type'
        />
      </Content>
      <Content center={true} short={true}>
        <Title level={2}>Common Types of Business Insurance</Title>
        <Paragraph>
          An insurance agent shouldn’t try to push a one-size-fits-all insurance policy for your business—you should be given a
          comprehensive overview of your business’s risk profile, the most appropriate coverage recommendation, and specific considerations for your industry or niche.
          Common policy types you may discover while shopping for business insurance could include:
        </Paragraph>
        <Title level={3}>Small Business Insurance</Title>
        <Paragraph>
          Also known as commercial insurance, small business insurance is designed for physical locations such as retail shops.
          This type of business insurance policy can cover business interruption, protect business assets, and anything that could impose financial losses on a business.
          Small business owners should make sure that their <Link to='/blog/posts/which-types-of-insurance-does-my-small-business-need'>small business insurance</Link> policy includes specific additional
          coverages that address specific business needs according to their industry, customer base, location, or other details.
        </Paragraph>
        <Title level={3}>General Liability Insurance</Title>
        <Paragraph>
          General liability is a form of business insurance policy that includes basic coverage that offers protection against losses incurred by a third party,
          whether you’re operating from your home or commercial property. Claims filed against a general liability policy can cover damages
          that otherwise would have been paid out by the business owner.
        </Paragraph>
        <Title level={3}>Workers Compensation Plan</Title>
        <Paragraph>
          A workers compensation insurance plan comes in handy for business owners with one or more employees.
          This form of insurance can offer wage compensation and medical benefits if an employee becomes injured or ill on the job.
          This insurance plan provides these benefits to the injured party in exchange for mandatory relinquishment of the employee’s right to sue the employer.
        </Paragraph>
        <Title level={3}>Commercial Vehicle Insurance</Title>
        <Paragraph>
          This provides insurance coverage for commercial vehicles or vehicles used specifically for business purposes.
          It can also extend to the party driving the vehicle, even if they aren’t the registered vehicle owner.
          This type of insurance policy is beneficial for businesses with commercial fleets used to carry out necessary transport or commute-related operations.
        </Paragraph>
      </Content>
    </Content>
    <Content margined={true} center={true} short={true}>
      <Title level={2}>Why Choose Insurance Lounge as Your Business Insurance Company?</Title>
      <Paragraph>
        Our founder and CEO, Michael DeLaGrange, wanted to create a transparent insurance buying experience with accessible policies that fit anyone’s needs.
        Insurance Lounge focuses on bringing simplicity back to insurance by focusing on technology, innovation, and figuring out ways to make the insurance buying experience more efficient.
        Led by a group of capable employees with backgrounds in management, insurance sales, customer service, and technology,
        our team has the knowledge to help business owners with property damage, liability coverage, handling assets, and many other business needs.
      </Paragraph>
    </Content>
    <Content theme={ContentTheme.yellow}>
      <Content short={true} center={true}>
        <Title level={2}>Frequently Asked Questions</Title>
        <Faq
          items={[
            {
              question: 'At what point does my online store need business insurance?',
              answer: (
                <Paragraph>It’s suggested that you purchase business insurance no matter the size of your online store.
                  Even though you don’t have an actual storefront, business insurance is still necessary to keep you and your products protected.
                </Paragraph>
              ),
            },
            {
              question: 'How do franchise businesses insure their franchise?',
              answer: (
                <>
                  <Paragraph>
                    Like any other business, franchises carry business insurance. But because franchises usually are bigger than single stand-alone businesses,
                    an insurance agent might also suggest:
                  </Paragraph>
                  <List theme={ListTheme.contentList}>
                    <li>
                      Equipment breakdown insurance
                    </li>
                    <li>
                      Loss of business income insurance
                    </li>
                    <li>
                      Commercial auto liability insurance (if the business franchise has a company vehicle)
                    </li>
                    <li>
                      Excess liability coverage
                    </li>
                  </List>
                  <Paragraph>
                    The above insurances would also be a good fit for stand-alone businesses, but because franchises are normally much larger,
                    it is more common for them to carry multiple types.
                  </Paragraph>
                </>
              ),
            },
            {
              question: 'Should a startup have business insurance?',
              answer: (
                <>
                  <Paragraph>Yes, no matter the size of your business or the number of people you employ, business insurance is necessary.
                    While the law does require only certain types of businesses to have insurance, it is wise for every business to have some sort of business insurance plan.
                  </Paragraph>
                </>
              ),
            },
            {
              question: 'What Type of Business Insurance Do I Need?',
              answer: (
                <>
                  <Paragraph>It’s best practice to have a general idea of what kind of coverage you’re looking for before seeking out insurance agents to walk you through potential policies.
                    A qualified insurance agent should give you precise recommendations according to your industry and business size.
                    It’s helpful to explore coverage options by industry beforehand so you feel prepared for a conversation with an
                    insurance provider and can discuss your needs from an informed perspective.
                  </Paragraph>
                </>
              ),
            },
          ]}
        />
      </Content>
    </Content>
    <Content margined={true} center={true} short={true}>
      <Title level={2}>Explore Your Options with Insurance Lounge</Title>
      <Paragraph>
        Insurance Lounge is a no-commission insurance agency offering a wide variety of policies for clients across Oregon.
        To make the shopping process simpler for you, we offer three convenient ways to get an insurance quote: <Link to='/quote/new'>online</Link>, over the phone,
        or in person at one of our retail locations throughout Oregon. Complete our quote and buy form to get your personalized quote in minutes,
        or <Link to='/quote/new'>contact us today</Link> to learn more about your unique options for insurance coverage.
      </Paragraph>
    </Content>
    <SectionRule />
    <AskQuestion title='We’re Here, When You Need Us'>
      Reach out and an Insurance Concierge<sup>®</sup> will get in touch!
    </AskQuestion>
  </div>
)

export default ProductsBusinessInsurance
